import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Dropdown, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { IoAddSharp, IoOpenOutline, IoRefresh, IoTrash } from 'react-icons/io5';
import { Reducers } from 'store/types';
import { updateBudget } from 'store/budget/action';
import { BudgetData } from 'store/budget/type';
import { BudgetConfigDefaultDto, BudgetConfigDto, BudgetConfigValueType, BudgetDto, BudgetGeneralityDto, BudgetLineDto } from 'api/budgets/models/BudgetDto';
import BudgetsService from 'api/budgets/BudgetsService';
import ElementTypesService from 'api/elementTypes/ElementTypesService';
import { ElementTypeDto } from 'api/elementTypes/models/ElementTypeDto';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { ParameterSectionParameterType, ParameterSectionType } from 'common/components/parametersSections/models';
import { BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import ExpressionButton from 'common/components/expressionButton/ExpressionButton';
import ExpressionSystem, { ExpressionParameter, EXPRESSION_PARAMETER_MENTION_PREFIX, EXPRESSION_PARAMETER_MENTION_SUFFIX } from 'common/components/expressionButton/ExpressionSystem';
import ListingTable from 'common/components/listingTable/ListingTable';
import { ListingTableColumn, OrderData } from 'common/components/listingTable/models';
import { ColumnOrder, ColumnWidth } from 'common/components/listingTable/ListingTableCache';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import MoneyInput from 'common/components/moneyInput/MoneyInput';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import TextInput from 'common/components/textInput/TextInput';
import Button from 'common/components/button/Button';
import Storage from 'common/services/Storage';
import MathUtils from 'common/services/MathUtils';
import { DEFAULT_DECIMAL_PLACES } from 'Config';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import YesNoModal from 'common/components/modal/yesNoModal/YesNoModal';
import styles from './BudgetSummary.module.scss';

const CACHE_KEY = 'BUDGET_SUMMARY';

const MATERIAL_WEIGHT_PARAM_NAME = 'peso material consumido';

export interface SummaryRow {
    id: string;
    isTotalsRow?: boolean;
    number: string;
    name: string;
    quantity: number | null;
    unit: string;
    totalWeight: number;
    costPerKg: number;
    salePerKg: number;
    totalWithoutMargin: number;
    marginPercentage: number;
    totalWithMargin: number;
    decennialInsuranceValue: number;
    commissionValue: number;
    totalCostsWithoutInsurance: number;
    totalCosts: number;
    effectiveMarginValue: number;
    effectiveMarginPercentage: number;
    [key: string]: any;
}

export interface ElementTypeInfo {
    id: string;
    name: string;
    units: string[];
}

export interface GroupedParamInfo {
    parameterName: string;
    fieldKey: string;
    unitSymbol: string;
}

interface SummaryCacheData {
    columnsWidths: ColumnWidth<SummaryRow>[];
    order: OrderData<SummaryRow>;
}

interface Props {
    hiddenColumns: string[];
    columnsOrder: ColumnOrder<SummaryRow>[];
    isDetails?: boolean;
    onNavigateToLine?: (lineKey: string) => void;
}

/**
 * Checks if a section only has SECTION ancestors (no ELEMENT in the ancestor chain).
 * This ensures we only include top-level elements, not elements inside sub-sections
 * that are nested within other elements.
 */
function isSectionWithOnlySectionAncestors (sectionKey: string, linesByKey: Map<string, BudgetLineDto>): boolean {
    let current = linesByKey.get(sectionKey);
    while (current) {
        if (current.lineType !== BudgetLineType.SECTION) {
            return false;
        }
        if (!current.parentKey) {
            return true;
        }
        current = linesByKey.get(current.parentKey);
    }
    return true;
}

/**
 * Collects all descendant elements (non-SECTION, non-NOTE, non-PAGE_BREAK) of a given line,
 * traversing the full hierarchy recursively (through sections and elements alike).
 */
function getDescendantElements (parentKey: string, lines: BudgetLineDto[]): BudgetLineDto[] {
    const result: BudgetLineDto[] = [];
    const directChildren = lines.filter(l => l.parentKey === parentKey);
    for (const child of directChildren) {
        if (child.lineType === BudgetLineType.NOTE || child.lineType === BudgetLineType.PAGE_BREAK) {
            continue;
        }
        if (child.lineType === BudgetLineType.SECTION) {
            result.push(...getDescendantElements(child.key, lines));
        } else {
            result.push(child);
            result.push(...getDescendantElements(child.key, lines));
        }
    }
    return result;
}

/**
 * Recursively collects all WEIGHT parameters from a parameter tree.
 */
function collectWeightParams (params: BudgetLineParameterDto[]): BudgetLineParameterDto[] {
    const result: BudgetLineParameterDto[] = [];
    for (const p of params) {
        if (p.type === ParameterSectionType.PARAMETER && p.parameterType === ParameterSectionParameterType.WEIGHT) {
            result.push(p);
        }
        if (p.children && p.children.length > 0) {
            result.push(...collectWeightParams(p.children));
        }
    }
    return result;
}

/**
 * Extracts the numeric value from a parameter based on its type.
 */
function getParameterNumericValue (p: BudgetLineParameterDto): number {
    switch (p.parameterType) {
        case ParameterSectionParameterType.NUMBER:
        case ParameterSectionParameterType.WEIGHT:
            return p.number?.number ?? 0;
        case ParameterSectionParameterType.QUANTITY:
            return p.quantity?.quantity ?? 0;
        case ParameterSectionParameterType.PRICE:
            return p.price?.price ?? 0;
        case ParameterSectionParameterType.NUMBER_MIN_MAX:
            return p.minMax?.value ?? 0;
        default:
            return 0;
    }
}

/**
 * Recursively collects all parameters marked with groupInSummary from a parameter tree.
 */
function collectGroupInSummaryParams (params: BudgetLineParameterDto[]): BudgetLineParameterDto[] {
    const result: BudgetLineParameterDto[] = [];
    for (const p of params) {
        if (p.type === ParameterSectionType.PARAMETER && p.groupInSummary === true) {
            result.push(p);
        }
        if (p.children && p.children.length > 0) {
            result.push(...collectGroupInSummaryParams(p.children));
        }
    }
    return result;
}

function getSummaryLines (lines: BudgetLineDto[], linesByKey: Map<string, BudgetLineDto>): BudgetLineDto[] {
    return lines.filter(line => {
        if (line.lineType === BudgetLineType.SECTION ||
            line.lineType === BudgetLineType.NOTE ||
            line.lineType === BudgetLineType.PAGE_BREAK) {
            return false;
        }
        if (!line.parentKey) return false;
        const parent = linesByKey.get(line.parentKey);
        if (parent?.lineType !== BudgetLineType.SECTION) return false;
        return isSectionWithOnlySectionAncestors(line.parentKey, linesByKey);
    });
}

export function getElementTypesFromLines (lines: BudgetLineDto[]): ElementTypeInfo[] {
    const linesByKey = new Map<string, BudgetLineDto>();
    lines.forEach(line => {
        linesByKey.set(line.key, line);
    });

    const summaryLines = getSummaryLines(lines, linesByKey);

    const elementTypesMap = new Map<string, ElementTypeInfo>();

    summaryLines.forEach(summaryLine => {
        const descendants = [summaryLine, ...getDescendantElements(summaryLine.key, lines)];
        descendants.forEach((desc: BudgetLineDto) => {
            if (desc.elementTypeName && desc.elementTypeId) {
                if (!elementTypesMap.has(desc.elementTypeId)) {
                    elementTypesMap.set(desc.elementTypeId, {
                        id: desc.elementTypeId,
                        name: desc.elementTypeName,
                        units: desc.unitSymbol ? [desc.unitSymbol] : [],
                    });
                } else if (desc.unitSymbol) {
                    const existing = elementTypesMap.get(desc.elementTypeId)!;
                    if (!existing.units.some(u => u.toLowerCase() === desc.unitSymbol!.toLowerCase())) {
                        existing.units.push(desc.unitSymbol);
                    }
                }
            }
        });
    });

    return Array.from(elementTypesMap.values());
}

export function getGroupedParamsFromLines (lines: BudgetLineDto[]): GroupedParamInfo[] {
    const linesByKey = new Map<string, BudgetLineDto>();
    lines.forEach(line => linesByKey.set(line.key, line));
    const summaryLines = getSummaryLines(lines, linesByKey);

    const seen = new Map<string, { name: string; unitSymbol: string }>();

    summaryLines.forEach(summaryLine => {
        const descendants = [summaryLine, ...getDescendantElements(summaryLine.key, lines)];
        descendants.forEach(desc => {
            const groupParams = collectGroupInSummaryParams(desc.parameters ?? []);
            groupParams.forEach(gp => {
                const key = (gp.parameterName ?? '').toLowerCase().trim();
                if (key && !seen.has(key)) {
                    seen.set(key, { name: gp.parameterName ?? '', unitSymbol: gp.unitSymbol ?? '' });
                }
            });
        });
    });

    return Array.from(seen.entries()).map(([key, info]) => ({
        parameterName: info.name,
        fieldKey: `gp_${key.replace(/[^a-z0-9_]/g, '_')}`,
        unitSymbol: info.unitSymbol,
    }));
}

export function getSummaryColumnFieldNames (elementTypes: ElementTypeInfo[], groupedParams: GroupedParamInfo[], t: (key: string) => string): { field: string; name: string }[] {
    const cols: { field: string; name: string }[] = [
        { field: 'number', name: t('budgets.budget.summary.point') },
        { field: 'name', name: t('budgets.budget.summary.name') },
        { field: 'quantity', name: t('budgets.budget.summary.quantity') },
        { field: 'unit', name: t('budgets.budget.summary.unit') },
    ];

    elementTypes.forEach(et => {
        et.units.forEach(unit => {
            cols.push({
                field: `et_${et.id}_qty_${unit.toLowerCase()}`,
                name: `${et.name} (${unit})`,
            });
        });
        cols.push({
            field: `et_${et.id}_total`,
            name: `${t('budgets.budget.summary.total_price')} ${et.name}`,
        });
    });

    const materialWeightParam = groupedParams.find(gp => gp.parameterName.toLowerCase().trim() === MATERIAL_WEIGHT_PARAM_NAME);
    const otherGroupedParams = groupedParams.filter(gp => gp.parameterName.toLowerCase().trim() !== MATERIAL_WEIGHT_PARAM_NAME);

    otherGroupedParams.forEach(gp => {
        cols.push({
            field: gp.fieldKey,
            name: gp.parameterName,
        });
    });

    cols.push({ field: 'totalWeight', name: t('budgets.budget.summary.total_weight') });
    if (materialWeightParam) {
        cols.push({ field: materialWeightParam.fieldKey, name: materialWeightParam.parameterName });
    }
    cols.push(
        { field: 'costPerKg', name: t('budgets.budget.summary.cost_per_kg') },
        { field: 'salePerKg', name: t('budgets.budget.summary.sale_per_kg') },
        { field: 'totalWithoutMargin', name: t('budgets.budget.summary.total_without_margin') },
        { field: 'marginPercentage', name: t('budgets.budget.summary.margin_percentage') },
        { field: 'totalWithMargin', name: t('budgets.budget.summary.total_with_margin') },
        { field: 'decennialInsuranceValue', name: t('budgets.budget.summary.decennial_insurance') },
        { field: 'commissionValue', name: t('budgets.budget.summary.commission') },
        { field: 'effectiveMarginValue', name: t('budgets.budget.summary.effective_margin_value') },
        { field: 'effectiveMarginPercentage', name: t('budgets.budget.summary.effective_margin_percentage') },
        { field: 'totalCostsWithoutInsurance', name: t('budgets.budget.summary.total_costs_without_insurance') },
        { field: 'totalCosts', name: t('budgets.budget.summary.total_costs') },
    );

    return cols;
}

function getFormulaParameters (row: SummaryRow, elementTypes: ElementTypeInfo[], groupedParams: GroupedParamInfo[], t: (key: string) => string): ExpressionParameter[] {
    const params: ExpressionParameter[] = [
        {
            id: ExpressionSystem.formatParameterId(['quantity']),
            name: t('budgets.budget.summary.quantity'),
            value: row.quantity,
        },
        {
            id: ExpressionSystem.formatParameterId(['totalWeight']),
            name: t('budgets.budget.summary.total_weight'),
            value: row.totalWeight,
        },
        {
            id: ExpressionSystem.formatParameterId(['costPerKg']),
            name: t('budgets.budget.summary.cost_per_kg'),
            value: row.costPerKg,
        },
        {
            id: ExpressionSystem.formatParameterId(['salePerKg']),
            name: t('budgets.budget.summary.sale_per_kg'),
            value: row.salePerKg,
        },
        {
            id: ExpressionSystem.formatParameterId(['totalWithoutMargin']),
            name: t('budgets.budget.summary.total_without_margin'),
            value: row.totalWithoutMargin,
        },
        {
            id: ExpressionSystem.formatParameterId(['marginPercentage']),
            name: t('budgets.budget.summary.margin_percentage'),
            value: row.marginPercentage,
        },
        {
            id: ExpressionSystem.formatParameterId(['totalWithMargin']),
            name: t('budgets.budget.summary.total_with_margin'),
            value: row.totalWithMargin,
        },
        {
            id: ExpressionSystem.formatParameterId(['decennialInsuranceValue']),
            name: t('budgets.budget.summary.decennial_insurance'),
            value: row.decennialInsuranceValue,
        },
        {
            id: ExpressionSystem.formatParameterId(['commissionValue']),
            name: t('budgets.budget.summary.commission'),
            value: row.commissionValue,
        },
        {
            id: ExpressionSystem.formatParameterId(['totalCostsWithoutInsurance']),
            name: t('budgets.budget.summary.total_costs_without_insurance'),
            value: row.totalCostsWithoutInsurance,
        },
        {
            id: ExpressionSystem.formatParameterId(['totalCosts']),
            name: t('budgets.budget.summary.total_costs'),
            value: row.totalCosts,
        },
        {
            id: ExpressionSystem.formatParameterId(['effectiveMarginValue']),
            name: t('budgets.budget.summary.effective_margin_value'),
            value: row.effectiveMarginValue,
        },
        {
            id: ExpressionSystem.formatParameterId(['effectiveMarginPercentage']),
            name: t('budgets.budget.summary.effective_margin_percentage'),
            value: row.effectiveMarginPercentage,
        },
    ];

    elementTypes.forEach(et => {
        et.units.forEach(unit => {
            const qtyKey = `et_${et.id}_qty_${unit.toLowerCase()}`;
            params.push({
                id: ExpressionSystem.formatParameterId([qtyKey]),
                name: `${et.name} (${unit})`,
                value: row[qtyKey] ?? 0,
            });
        });
        params.push({
            id: ExpressionSystem.formatParameterId([`et_${et.id}_total`]),
            name: `${t('budgets.budget.summary.total_price')} ${et.name}`,
            value: row[`et_${et.id}_total`] ?? 0,
        });
    });

    groupedParams.forEach(gp => {
        params.push({
            id: ExpressionSystem.formatParameterId([gp.fieldKey]),
            name: gp.parameterName,
            value: row[gp.fieldKey] ?? 0,
        });
    });

    return params;
}

// Prefixo dos ids de parâmetro das generalidades específicas do orçamento (posicional, ex.:
// generality_0__quantity). Estas não existem noutros orçamentos, por isso são limpas ao guardar o
// template global (ver stripGeneralityReferences).
export const GENERALITY_PARAM_PREFIX = 'generality_';
// Prefixo dos ids das generalidades vindas do backoffice (com sourceGeneralityDefaultId). Este id é
// estável entre orçamentos, por isso as referências sobrevivem à reutilização e NÃO são limpas.
export const GENERALITY_DEFAULT_PARAM_PREFIX = 'gen_default_';

// Expõe cada linha de generalidade do orçamento (quantidade, preço unitário e os 3 totais) como
// parâmetro de fórmula no Resumo. Espelha buildExpressionParameters da tab de Generalidades, mas
// sem o conceito de "linha atual" — aqui todas as linhas são externas. As generalidades de
// backoffice são referenciadas pelo id estável (sourceGeneralityDefaultId) para a fórmula poder ser
// reutilizada noutros orçamentos; as específicas do orçamento usam o índice posicional.
function getGeneralityParameters (generalities: BudgetGeneralityDto[], t: (key: string) => string): ExpressionParameter[] {
    const params: ExpressionParameter[] = [];
    const generalityName = t('budgets.budget.tab_generalities');

    generalities.forEach((g, idx) => {
        const lineKey = g.sourceGeneralityDefaultId
            ? `${GENERALITY_DEFAULT_PARAM_PREFIX}${g.sourceGeneralityDefaultId}`
            : `${GENERALITY_PARAM_PREFIX}${idx}`;
        const designation = g.designation || `${t('budgets.budget.generalities.header.designation')} #${idx + 1}`;

        const lineFields: Array<[string, string, number | null | undefined]> = [
            ['quantity', t('budgets.budget.generalities.header.quantity'), g.quantity],
            ['unitPrice', t('budgets.budget.generalities.header.unit_price'), g.unitPrice],
            ['totalExcludingTax', t('budgets.budget.generalities.line_total.excluding_tax'), g.totalExcludingTax],
            ['totalTax', t('budgets.budget.generalities.line_total.tax'), g.totalTax],
            ['totalIncludingTax', t('budgets.budget.generalities.line_total.including_tax'), g.totalIncludingTax],
        ];

        for (const [id, name, value] of lineFields) {
            params.push({
                id: ExpressionSystem.formatParameterId([lineKey, id]),
                name: ExpressionSystem.formatParameterName([generalityName, designation, name]),
                value: value ?? 0,
            });
        }

        // Campos personalizados de cada generalidade — espelha buildExpressionParameters da tab
        // de Generalidades. Usam o mesmo lineKey (estável para backoffice, posicional para as
        // específicas do orçamento) com o segmento "field" + a key do campo.
        for (const fld of g.fields ?? []) {
            params.push({
                id: ExpressionSystem.formatParameterId([lineKey, 'field', fld.fieldKey]),
                name: ExpressionSystem.formatParameterName([generalityName, designation, fld.designation || fld.fieldKey]),
                value: fld.value ?? 0,
            });
        }
    });

    return params;
}

// Substitui por 0 apenas as referências a generalidades ESPECÍFICAS do orçamento (posicionais,
// `generality_{idx}`). Usado ao guardar os campos de resumo como template global (config-defaults):
// estas generalidades não existem noutro orçamento e dariam NOT_FOUND_ERROR ao reutilizar.
// As generalidades de backoffice (`gen_default_{id}`) têm id estável e EXISTEM noutros orçamentos,
// por isso não são tocadas. Totais agregados (budget__...) e restantes variáveis também não.
export function stripGeneralityReferences (expression: string | null | undefined): string {
    if (!expression) return '';
    const tokenPrefix = EXPRESSION_PARAMETER_MENTION_PREFIX + GENERALITY_PARAM_PREFIX;
    const escapedPrefix = tokenPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Cobre tanto os campos padrão (generality_0__quantity) como os campos personalizados
    // (generality_0__field__<key>), ambos posicionais e portanto inexistentes noutros orçamentos.
    const regex = new RegExp(`${escapedPrefix}\\d+__[0-9A-Za-z_]+${EXPRESSION_PARAMETER_MENTION_SUFFIX}`, 'g');
    return expression.replace(regex, '0');
}

function evaluateFormula (expression: string | undefined, parameters: ExpressionParameter[]): number {
    if (!expression) return 0;
    const result = ExpressionSystem.evaluate(expression, parameters);
    return result.error ? 0 : result.value;
}

const CONFIG_DEFINITIONS = [
    {
        key: 'total_costs_sage',
        translationKey: 'budgets.budget.summary.config_total_costs_sage',
        unitPrefix: '€',
        defaultExpressionFn: (ets: ElementTypeInfo[]) => {
            const etParts = ets.map(et =>
                ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId([`et_${et.id}_total`])
                )
            );
            const insurancePart = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['decennialInsuranceValue'])
            );
            const commissionPart = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['commissionValue'])
            );
            const executionStudyCost = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['execution_study_cost'])
            );
            const insertionCost = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['insertion_cost'])
            );
            const expenses = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['expenses'])
            );
            const allParts = [...etParts, insurancePart, commissionPart, executionStudyCost, insertionCost, expenses];
            if (allParts.length === 0) return '';
            return allParts.join(' + ');
        },
    },
    { key: 'total_sale_value', translationKey: 'budgets.budget.summary.config_total_sale_value', unitPrefix: '€', defaultExpression: 'M_budget__totalExcludingTax_E' },
    { key: 'ml_scope_percentage', translationKey: 'budgets.budget.summary.config_ml_scope_percentage', unitPrefix: '%', defaultExpression: '100 * SE(M_total_costs_plus_ee_hi_expenses_E = 0; 0; SE(M_total_sale_value_E = 0; 0; 100% - (M_total_costs_plus_ee_hi_expenses_E / M_total_sale_value_E)))' },
    { key: 'technical_drawing_revenue', translationKey: 'budgets.budget.summary.config_technical_drawing_revenue', unitPrefix: '€' },
    { key: 'total_assembly_revenue', translationKey: 'budgets.budget.summary.config_total_assembly_revenue', unitPrefix: '€', defaultExpression: 'M_total_mo_assembly_expenses_ins_E + ((SOMA(M_decennialInsuranceValue_E; M_commissionValue_E) * (1 + M_effectiveMarginPercentage_E / 100)) * M_assembly_percentage_E / 100)' },
    { key: 'assembly_percentage', translationKey: 'budgets.budget.summary.config_assembly_percentage', unitPrefix: '%', defaultExpression: 'SE(M_total_mo_assembly_expenses_ins_E = 0; 0; SE(M_total_sale_value_E = 0; 0; SE(M_technical_drawing_revenue_E = 0; 0; (100% * M_total_mo_assembly_expenses_ins_E) / (M_total_sale_value_E - M_technical_drawing_revenue_E))))' },
    { key: 'total_manufacturing_revenue', translationKey: 'budgets.budget.summary.config_total_manufacturing_revenue', unitPrefix: '€', defaultExpression: 'M_total_manufacturing_2_E + ((SOMA(M_decennialInsuranceValue_E; M_commissionValue_E) * (1 + M_effectiveMarginPercentage_E / 100)) * M_manufacturing_percentage_E / 100)' },
    { key: 'manufacturing_percentage', translationKey: 'budgets.budget.summary.config_manufacturing_percentage', unitPrefix: '%', defaultExpression: '100 - M_assembly_percentage_E' },
    { key: 'manufacturing_hours', translationKey: 'budgets.budget.summary.config_manufacturing_hours', unitPrefix: '€' },
    { key: 'assembly_hours', translationKey: 'budgets.budget.summary.config_assembly_hours', unitPrefix: '€' },
    { key: 'technician_cost', translationKey: 'budgets.budget.summary.config_technician_cost', unitPrefix: '€' },
    { key: 'insertion_hours', translationKey: 'budgets.budget.summary.config_insertion_hours', unitPrefix: '€' },
    { key: 'execution_study_cost', translationKey: 'budgets.budget.summary.config_execution_study_cost', unitPrefix: '€' },
    { key: 'execution_study_hours', translationKey: 'budgets.budget.summary.config_execution_study_hours', defaultExpression: 'ROUNDDOWN(M_execution_study_cost_E / M_technician_cost_E; 0)', unitPrefix: 'H' },
    { key: 'insertion_hours_total', translationKey: 'budgets.budget.summary.config_insertion_hours_total', unitPrefix: 'H' },
    { key: 'insertion_cost', translationKey: 'budgets.budget.summary.config_insertion_cost', defaultExpression: 'M_insertion_hours_total_E * M_insertion_hours_E', unitPrefix: '€' },
    { key: 'expenses', translationKey: 'budgets.budget.summary.config_expenses', unitPrefix: '€' },
    { key: 'insertion_plus_expenses', translationKey: 'budgets.budget.summary.config_insertion_plus_expenses', defaultExpression: 'M_insertion_cost_E + M_expenses_E', unitPrefix: '€' },
    {
        key: 'total_assembly',
        translationKey: 'budgets.budget.summary.config_total_assembly',
        unitPrefix: '€',
        defaultExpressionFn: (ets: ElementTypeInfo[]) => {
            const assemblyEt = ets.find(et => et.name.trim().toLowerCase() === 'montagem');
            if (!assemblyEt) return '';
            const assemblyTotal = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId([`et_${assemblyEt.id}_total`])
            );
            const insertionCost = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['insertion_cost'])
            );
            const expenses = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['expenses'])
            );
            const decennial = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['decennialInsuranceValue'])
            );
            const commission = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['commissionValue'])
            );
            const assemblyPct = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['assembly_percentage'])
            );
            return `(${assemblyTotal} + ${insertionCost} + ${expenses}) + (SOMA(${decennial} + ${commission}) * (${assemblyPct} / 100))`;
        },
    },
    {
        key: 'total_manufacturing',
        translationKey: 'budgets.budget.summary.config_total_manufacturing',
        unitPrefix: '€',
        defaultExpressionFn: (ets: ElementTypeInfo[]) => {
            const targetNames = ['fabrico', 'materiais', 'tratamentos', 'transporte'];
            const etTotals = targetNames
                .map(name => ets.find(et => et.name.trim().toLowerCase() === name))
                .filter((et): et is ElementTypeInfo => !!et)
                .map(et => ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId([`et_${et.id}_total`])
                ));
            if (etTotals.length === 0) return '';
            const decennial = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['decennialInsuranceValue'])
            );
            const commission = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['commissionValue'])
            );
            const manufacturingPct = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['manufacturing_percentage'])
            );
            return `(${etTotals.join(' + ')}) + (SOMA(${decennial} + ${commission}) * (${manufacturingPct} / 100))`;
        },
    },
    { key: 'total_assembly_manufacturing_studies', translationKey: 'budgets.budget.summary.config_total_assembly_manufacturing_studies', defaultExpression: 'M_total_assembly_E + M_total_manufacturing_E + M_execution_study_cost_E', unitPrefix: '€' },
    { key: 'ee_hi_expenses', translationKey: 'budgets.budget.summary.config_ee_hi_expenses', defaultExpression: 'M_execution_study_cost_E + M_insertion_cost_E + M_expenses_E', unitPrefix: '€' },
    { key: 'total_costs_plus_ee_hi_expenses', translationKey: 'budgets.budget.summary.config_total_costs_plus_ee_hi_expenses', defaultExpression: 'M_ee_hi_expenses_E + M_total_costs_sage_E', unitPrefix: '€' },
    { key: 'commissions_to_pay', translationKey: 'budgets.budget.summary.config_commissions_to_pay', defaultExpression: '(M_ee_hi_expenses_E + M_totalWithMargin_E) * M_defaultCommissionMarginPercentage_E%', unitPrefix: '€' },
    { key: 'commercial_discount', translationKey: 'budgets.budget.summary.config_commercial_discount', unitPrefix: '€', defaultExpression: 'M_budget__discount_E' },
    {
        key: 'total_mo_assembly_expenses_ins',
        translationKey: 'budgets.budget.summary.config_total_mo_assembly_expenses_ins',
        unitPrefix: '€',
        defaultExpressionFn: (ets: ElementTypeInfo[]) => {
            const assemblyEt = ets.find(et => et.name.trim().toLowerCase() === 'montagem');
            if (!assemblyEt) return '';
            const assemblyTotal = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId([`et_${assemblyEt.id}_total`])
            );
            const effectiveMargin = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['effectiveMarginPercentage'])
            );
            const insertionCost = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['insertion_cost'])
            );
            const executionStudyCost = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['execution_study_cost'])
            );
            return `${assemblyTotal} * (1 + ${effectiveMargin}%) + ${insertionCost} + ${executionStudyCost}`;
        },
    },
    {
        key: 'total_manufacturing_2',
        translationKey: 'budgets.budget.summary.config_total_manufacturing_2',
        unitPrefix: '€',
        defaultExpressionFn: (ets: ElementTypeInfo[]) => {
            const targetNames = ['fabrico', 'materiais', 'tratamentos', 'transporte'];
            const etTotals = targetNames
                .map(name => ets.find(et => et.name.trim().toLowerCase() === name))
                .filter((et): et is ElementTypeInfo => !!et)
                .map(et => ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId([`et_${et.id}_total`])
                ));
            if (etTotals.length === 0) return '';
            const effectiveMargin = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['effectiveMarginPercentage'])
            );
            return `(${etTotals.join(' + ')}) * (1 + ${effectiveMargin}%)`;
        },
    },
    {
        key: 'total_costs_effective_ml_expenses',
        translationKey: 'budgets.budget.summary.config_total_costs_effective_ml_expenses',
        unitPrefix: '€',
        defaultExpressionFn: (ets: ElementTypeInfo[]) => {
            const targetNames = ['fabrico', 'montagem', 'materiais', 'tratamentos', 'transporte'];
            const etTotals = targetNames
                .map(name => ets.find(et => et.name.trim().toLowerCase() === name))
                .filter((et): et is ElementTypeInfo => !!et)
                .map(et => ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId([`et_${et.id}_total`])
                ));
            if (etTotals.length === 0) return '';
            const decennial = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['decennialInsuranceValue'])
            );
            const commission = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['commissionValue'])
            );
            const effectiveMargin = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['effectiveMarginPercentage'])
            );
            const technicalDrawing = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['technical_drawing_revenue'])
            );
            const insertionCost = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['insertion_cost'])
            );
            const expenses = ExpressionSystem.serializeParameterMentionId(
                ExpressionSystem.formatParameterId(['expenses'])
            );
            const somaParts = [...etTotals, decennial, commission].join('; ');
            return `ARRED((SOMA(${somaParts})) * (1 + ${effectiveMargin}%) + ${technicalDrawing} + ${insertionCost} + ${expenses}; 4)`;
        },
    },
    { key: 'total_revenue', translationKey: 'budgets.budget.summary.config_total_revenue', unitPrefix: '€', defaultExpression: 'ARRED(SOMA(M_technical_drawing_revenue_E + M_total_assembly_revenue_E + M_total_manufacturing_revenue_E); 4)' },
    { key: 'insurance_assembly', translationKey: 'budgets.budget.summary.config_insurance_assembly', unitPrefix: '€', defaultExpression: 'M_decennialInsuranceValue_E * M_assembly_percentage_E%' },
    { key: 'insurance_manufacturing', translationKey: 'budgets.budget.summary.config_insurance_manufacturing', unitPrefix: '€', defaultExpression: 'M_decennialInsuranceValue_E * M_manufacturing_percentage_E%' },
    { key: 'commission_assembly', translationKey: 'budgets.budget.summary.config_commission_assembly', unitPrefix: '€', defaultExpression: 'M_commissionValue_E * M_assembly_percentage_E%' },
    { key: 'commission_manufacturing', translationKey: 'budgets.budget.summary.config_commission_manufacturing', unitPrefix: '€', defaultExpression: 'M_commissionValue_E * M_manufacturing_percentage_E%' },
];

export function getConfigFieldNames (t: (key: string) => string, configs: BudgetConfigDto[] = []): { field: string; name: string }[] {
    const predefinedByKey = new Map(CONFIG_DEFINITIONS.map(d => [d.key, d]));
    const activeKeys = new Set(configs.map(c => c.key));

    const active = configs.map(c => {
        if (c.label && c.label.trim().length > 0) {
            return { field: c.key, name: c.label };
        }
        const def = predefinedByKey.get(c.key);
        if (def) {
            return { field: c.key, name: t(def.translationKey) };
        }
        return { field: c.key, name: t('budgets.budget.summary.custom_config') };
    });

    const inactivePredefined = CONFIG_DEFINITIONS
        .filter(def => !activeKeys.has(def.key))
        .map(def => ({
            field: def.key,
            name: t(def.translationKey),
        }));

    return [...active, ...inactivePredefined];
}

export function buildPredefinedConfigEntry (key: string, globalDefaults: BudgetConfigDefaultDto[], elementTypes: ElementTypeInfo[]): BudgetConfigDto {
    const def = CONFIG_DEFINITIONS.find(d => d.key === key);
    if (!def) {
        return { key, expression: '', valueType: BudgetConfigValueType.NUMBER };
    }
    const globalDefault = globalDefaults.find(g => g.key === key);
    const hardcoded = (def as any).defaultExpressionFn
        ? (def as any).defaultExpressionFn(elementTypes)
        : ((def as any).defaultExpression as string | undefined);
    const expression = (globalDefault?.expression || undefined) ?? hardcoded ?? '';
    return { key, expression, valueType: BudgetConfigValueType.NUMBER };
}

function BudgetSummary ({ hiddenColumns, columnsOrder, isDetails, onNavigateToLine }: Props): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const lines = useSelector<Reducers, BudgetLineDto[]>(state => state.budget.lines);
    const decimalPlaces = useSelector<Reducers, number>(x => x.budget.budget.decimalPlaces ?? DEFAULT_DECIMAL_PLACES);
    const decennialInsuranceExpression = useSelector<Reducers, string | undefined>(x => x.budget.budget.decennialInsuranceExpression);
    const commissionExpression = useSelector<Reducers, string | undefined>(x => x.budget.budget.commissionExpression);
    const defaultCommissionMarginPercentage = useSelector<Reducers, number>(x => x.budget.budget.defaultCommissionMarginPercentage ?? 0);
    const totalNetExcludingTax = useSelector<Reducers, number>(x => x.budget.budget.totalNetExcludingTax ?? 0);
    const linesTotalNetExcludingTax = useSelector<Reducers, number>(x => x.budget.budget.linesTotalNetExcludingTax ?? x.budget.budget.totalNetExcludingTax ?? 0);
    const totalExcludingTax = useSelector<Reducers, number>(x => x.budget.budget.totalExcludingTax ?? 0);
    const totalIncludingTax = useSelector<Reducers, number>(x => x.budget.budget.totalIncludingTax ?? 0);
    const budgetDiscount = useSelector<Reducers, number>(x => x.budget.budget.discount ?? 0);
    const defaultDiscountPercentage = useSelector<Reducers, number>(x => x.budget.budget.defaultDiscountPercentage ?? 0);
    const defaultMarginPercentage = useSelector<Reducers, number>(x => x.budget.budget.defaultMarginPercentage ?? 0);
    const defaultDecennialInsurancePercentage = useSelector<Reducers, number>(x => x.budget.budget.defaultDecennialInsurancePercentage ?? 0);
    const defaultTaxValue = useSelector<Reducers, number>(x => x.budget.budget.defaultTaxValue ?? 0);
    const configs = useSelector<Reducers, BudgetConfigDto[]>(x => x.budget.budget.configs ?? []);

    // As linhas de generalidades vivem no estado do form (não no Redux, que só guarda subtotais
    // agregados). O BudgetSummary é renderizado dentro do mesmo FormProvider, por isso pode lê-las
    // via useWatch — assim as opções de fórmula refletem edições sem reabrir a tab.
    const form = useFormContext<BudgetDto>();
    const generalities = useWatch({ control: form.control, name: 'generalities' }) ?? [];

    const [globalDefaults, setGlobalDefaults] = useState<BudgetConfigDefaultDto[]>([]);
    const [catalogElementTypes, setCatalogElementTypes] = useState<ElementTypeDto[]>([]);
    const [currentInfoTooltip, setCurrentInfoTooltip] = useState<{ title: string; message: string } | null>(null);
    const [restoreConfirmKey, setRestoreConfirmKey] = useState<string | null>(null);

    const fmt = (v: number) => Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: decimalPlaces });

    const fmtNum = (v: number) => MathUtils.round(v, decimalPlaces).toLocaleString('fr-FR', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });

    const withCalcTooltip = (content: JSX.Element | string, tooltipText: string, id: string) => {
        if (!tooltipText) return content;
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{tooltipText}</Tooltip>}>
                <span style={{ display: 'inline-block', width: '100%' }}>{content}</span>
            </OverlayTrigger>
        );
    };

    useEffect(() => {
        BudgetsService.getConfigDefaults()
            .then(result => setGlobalDefaults(Array.isArray(result) ? result : []))
            .catch(() => {});
        ElementTypesService.getCatalog()
            .then(result => setCatalogElementTypes(Array.isArray(result) ? result : []))
            .catch(() => {});
    }, []);

    const predefinedByKey = useMemo(() => new Map(CONFIG_DEFINITIONS.map(d => [d.key, d])), []);

    const customConfigs = useMemo(() => {
        return configs.filter(c => !predefinedByKey.has(c.key));
    }, [configs, predefinedByKey]);

    type RenderableConfig =
        | { kind: 'predefined'; key: string; def: typeof CONFIG_DEFINITIONS[number]; config: BudgetConfigDto }
        | { kind: 'custom'; key: string; config: BudgetConfigDto };

    const orderedConfigItems = useMemo<RenderableConfig[]>(() => {
        return configs.map(c => {
            const def = predefinedByKey.get(c.key);
            if (def) {
                return { kind: 'predefined' as const, key: c.key, def, config: c };
            }
            return { kind: 'custom' as const, key: c.key, config: c };
        });
    }, [configs, predefinedByKey]);

    const { rows, elementTypes, groupedParams, totalsRow } = useMemo(() => {
        const linesByKey = new Map<string, BudgetLineDto>();
        lines.forEach(line => {
            linesByKey.set(line.key, line);
        });

        const summaryLines = getSummaryLines(lines, linesByKey);

        const lineElementTypes = getElementTypesFromLines(lines);
        // Merge with catalog element types so all types are always present
        const mergedElementTypesMap = new Map<string, ElementTypeInfo>();
        catalogElementTypes.forEach(ct => {
            mergedElementTypesMap.set(ct.id, { id: ct.id, name: ct.name, units: [] });
        });
        lineElementTypes.forEach(et => {
            if (mergedElementTypesMap.has(et.id)) {
                mergedElementTypesMap.get(et.id)!.units = et.units;
            } else {
                mergedElementTypesMap.set(et.id, et);
            }
        });
        const elementTypesArr = Array.from(mergedElementTypesMap.values());
        const groupedParamsArr = getGroupedParamsFromLines(lines);
        const materialWeightParam = groupedParamsArr.find(gp =>
            gp.parameterName.toLowerCase().trim() === MATERIAL_WEIGHT_PARAM_NAME
        );

        const dataRows: SummaryRow[] = summaryLines.map(summaryLine => {
            const childDescendants = getDescendantElements(summaryLine.key, lines);
            const descendants = [summaryLine, ...childDescendants];

            const row: SummaryRow = {
                id: summaryLine.key,
                number: summaryLine.number,
                name: summaryLine.designation,
                quantity: summaryLine.quantity,
                unit: summaryLine.unitSymbol ?? '',
                totalWeight: 0,
                costPerKg: 0,
                salePerKg: 0,
                totalWithoutMargin: 0,
                marginPercentage: summaryLine.marginPercentage,
                totalWithMargin: 0,
                totalCostsWithoutInsurance: 0,
                totalCosts: 0,
                decennialInsuranceValue: 0,
                commissionValue: 0,
                effectiveMarginValue: 0,
                effectiveMarginPercentage: 0,
            };

            elementTypesArr.forEach(et => {
                const totalKey = `et_${et.id}_total`;
                const typeDescendants = descendants.filter(c => c.elementTypeId === et.id);

                et.units.forEach(unit => {
                    const qtyKey = `et_${et.id}_qty_${unit.toLowerCase()}`;
                    const unitDescendants = typeDescendants.filter(c => c.unitSymbol?.toLowerCase() === unit.toLowerCase());
                    row[qtyKey] = unitDescendants.reduce((sum, c) => sum + (c.quantity ?? 0), 0);
                });

                row[totalKey] = typeDescendants.reduce((sum, c) => sum + (c.grossUnitPrice * (c.quantity ?? 0)), 0);
            });

            row.totalCostsWithoutInsurance = elementTypesArr.reduce((sum, et) => sum + (row[`et_${et.id}_total`] ?? 0), 0);

            row.totalWeight = childDescendants.reduce((sum, desc) => {
                const weightParams = collectWeightParams(desc.parameters ?? []);
                const weight = weightParams.reduce((wSum, p) => wSum + (p.number?.number ?? 0), 0);
                return sum + weight * (desc.quantity ?? 0);
            }, 0);

            groupedParamsArr.forEach(gp => {
                const paramNameLower = gp.parameterName.toLowerCase().trim();
                row[gp.fieldKey] = childDescendants.reduce((sum, desc) => {
                    const matchingParams = collectGroupInSummaryParams(desc.parameters ?? [])
                        .filter(p => (p.parameterName ?? '').toLowerCase().trim() === paramNameLower);
                    const paramSum = matchingParams.reduce((pSum, p) => pSum + getParameterNumericValue(p), 0);
                    return sum + paramSum;
                }, 0);
            });

            const grossUnitPrice = summaryLine.grossUnitPrice ?? 0;
            const discountPercentage = summaryLine.discountPercentage ?? 0;
            const absoluteChildrenSum = summaryLine.absoluteChildrenSum ?? 0;
            const marginPercentage = summaryLine.marginPercentage ?? 0;
            const decennialInsurancePercentage = summaryLine.decennialInsurancePercentage ?? 0;
            const quantity = summaryLine.quantity ?? 0;
            const totalExcludingTax = summaryLine.totalExcludingTax ?? 0;

            const discountValue = grossUnitPrice * discountPercentage / 100;
            const childrenUnitSum = quantity > 0 ? absoluteChildrenSum / quantity : 0;
            const totalCostUnitPrice = grossUnitPrice - discountValue + childrenUnitSum;

            row.totalWithMargin = totalExcludingTax;
            row.totalWithoutMargin = totalCostUnitPrice * quantity;
            const materialWeight = materialWeightParam ? (row[materialWeightParam.fieldKey] ?? 0) : 0;
            row._materialWeight = materialWeight;
            row.costPerKg = materialWeight > 0 ? row.totalWithoutMargin / materialWeight : 0;
            row.salePerKg = materialWeight > 0 ? row.totalWithMargin / materialWeight : 0;

            row._salePriceUnit = totalCostUnitPrice;
            row._grossUnitPrice = grossUnitPrice;
            row._discountPercentage = discountPercentage;
            row._discountValue = discountValue;
            row._childrenUnitSum = childrenUnitSum;
            row._decennialInsurancePercentage = decennialInsurancePercentage;

            const priceWithMargin = totalCostUnitPrice * (1 + marginPercentage / 100);
            row._priceWithMargin = priceWithMargin;
            row.decennialInsuranceValue = priceWithMargin * (decennialInsurancePercentage / 100) * quantity;

            row.commissionValue = row.totalWithoutMargin * (defaultCommissionMarginPercentage / 100);

            return row;
        });

        const totals: SummaryRow = {
            id: '__totals__',
            isTotalsRow: true,
            number: '',
            name: '',
            quantity: 0,
            unit: '',
            totalWeight: 0,
            costPerKg: 0,
            salePerKg: 0,
            totalWithoutMargin: 0,
            marginPercentage: 0,
            totalWithMargin: 0,
            totalCostsWithoutInsurance: 0,
            totalCosts: 0,
            decennialInsuranceValue: 0,
            commissionValue: 0,
            effectiveMarginValue: 0,
            effectiveMarginPercentage: 0,
        };

        elementTypesArr.forEach(et => {
            const totalKey = `et_${et.id}_total`;
            et.units.forEach(unit => {
                totals[`et_${et.id}_qty_${unit.toLowerCase()}`] = 0;
            });
            totals[totalKey] = 0;
        });

        groupedParamsArr.forEach(gp => {
            totals[gp.fieldKey] = 0;
        });

        dataRows.forEach(row => {
            elementTypesArr.forEach(et => {
                const totalKey = `et_${et.id}_total`;
                et.units.forEach(unit => {
                    const qtyKey = `et_${et.id}_qty_${unit.toLowerCase()}`;
                    totals[qtyKey] = (totals[qtyKey] || 0) + (row[qtyKey] || 0);
                });
                totals[totalKey] = (totals[totalKey] || 0) + (row[totalKey] || 0);
            });
            groupedParamsArr.forEach(gp => {
                totals[gp.fieldKey] = (totals[gp.fieldKey] || 0) + (row[gp.fieldKey] || 0);
            });
            totals.totalWeight += row.totalWeight;
            totals.totalWithoutMargin += row.totalWithoutMargin;
            totals.totalWithMargin += row.totalWithMargin;
            totals.totalCostsWithoutInsurance += row.totalCostsWithoutInsurance;
            totals.decennialInsuranceValue += row.decennialInsuranceValue;
            totals.commissionValue += row.commissionValue;
        });

        const totalsMaterialWeight = materialWeightParam ? (totals[materialWeightParam.fieldKey] ?? 0) : 0;
        totals._materialWeight = totalsMaterialWeight;
        if (totalsMaterialWeight > 0) {
            totals.costPerKg = totals.totalWithoutMargin / totalsMaterialWeight;
            totals.salePerKg = totals.totalWithMargin / totalsMaterialWeight;
        }

        if (totals.totalWithoutMargin > 0) {
            totals.marginPercentage = ((totals.totalWithMargin / totals.totalWithoutMargin) - 1) * 100;
        }

        return {
            rows: dataRows,
            elementTypes: elementTypesArr,
            groupedParams: groupedParamsArr,
            totalsRow: totals,
        };
    }, [lines, defaultCommissionMarginPercentage, catalogElementTypes]);

    const allConfigDefs = useMemo(() => {
        const predefined = CONFIG_DEFINITIONS.map(def => {
            const globalDefault = globalDefaults.find(gd => gd.key === def.key);
            const hardcodedDefault = (def as any).defaultExpressionFn
                ? (def as any).defaultExpressionFn(elementTypes)
                : ((def as any).defaultExpression as string | undefined);
            const defaultExpression = (globalDefault?.expression || undefined) ?? hardcodedDefault;
            return {
                key: def.key,
                name: t(def.translationKey),
                defaultExpression,
                isCustom: false,
            };
        });
        const custom = customConfigs.map(c => ({
            key: c.key,
            name: c.label || c.key,
            defaultExpression: undefined as string | undefined,
            isCustom: true,
        }));
        return [...predefined, ...custom];
    }, [customConfigs, elementTypes, globalDefaults, t]);

    const totalsParams = useMemo(() => {
        const baseParams = getFormulaParameters(totalsRow, elementTypes, groupedParams, t);

        // Pre-compute total decennial insurance and commission so they're available in config formulas
        const totalDecennialInsurance = decennialInsuranceExpression
            ? rows.reduce((sum, row) => {
                const params = getFormulaParameters(row, elementTypes, groupedParams, t);
                return sum + evaluateFormula(decennialInsuranceExpression, params);
            }, 0)
            : rows.reduce((sum, row) => sum + row.decennialInsuranceValue, 0);
        const totalCommission = commissionExpression
            ? rows.reduce((sum, row) => {
                const params = getFormulaParameters(row, elementTypes, groupedParams, t);
                return sum + evaluateFormula(commissionExpression, params);
            }, 0)
            : rows.reduce((sum, row) => sum + row.commissionValue, 0);

        // Override decennialInsuranceValue and commissionValue with computed totals
        const diIdx = baseParams.findIndex(p => p.id === ExpressionSystem.formatParameterId(['decennialInsuranceValue']));
        if (diIdx >= 0) {
            baseParams[diIdx].value = totalDecennialInsurance;
        }
        const cvIdx = baseParams.findIndex(p => p.id === ExpressionSystem.formatParameterId(['commissionValue']));
        if (cvIdx >= 0) {
            baseParams[cvIdx].value = totalCommission;
        }

        baseParams.push({
            // Legacy unprefixed ID used by predefined CONFIG_DEFINITIONS expressions.
            // Hidden from the @ dropdown — users should pick the prefixed `Orçamento > ...` version below.
            id: ExpressionSystem.formatParameterId(['defaultCommissionMarginPercentage']),
            name: t('budgets.budget.default_commission_margin'),
            value: defaultCommissionMarginPercentage,
            hidden: true,
        });

        const budgetName = t('budgets.budget.budget');
        const budgetKey = 'budget';
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalNetExcludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_net_without_tax')]),
            value: totalNetExcludingTax,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'linesTotalNetExcludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_lines_without_tax')]),
            value: linesTotalNetExcludingTax,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalExcludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_without_tax')]),
            value: totalExcludingTax,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalIncludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_with_tax')]),
            value: totalIncludingTax,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'discount']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.discount')]),
            value: budgetDiscount,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultDiscountPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_remise')]),
            value: defaultDiscountPercentage,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultMarginPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_margin')]),
            value: defaultMarginPercentage,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultCommissionMarginPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_commission_margin')]),
            value: defaultCommissionMarginPercentage,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultCombinedMarginPercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_combined_margin')]),
            value: defaultMarginPercentage + defaultCommissionMarginPercentage,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultDecennialInsurancePercentage']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_decennial_insurance')]),
            value: defaultDecennialInsurancePercentage,
        });
        baseParams.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'defaultTaxValue']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.default_tax')]),
            value: defaultTaxValue,
        });

        // Override effectiveMarginPercentage with the computed total value
        const totalEffectiveMarginValue = totalsRow.totalWithMargin - totalsRow.totalWithoutMargin - totalDecennialInsurance - totalCommission;
        const totalDenominator = totalsRow.totalWithoutMargin + totalDecennialInsurance + totalCommission;
        const totalEffectiveMarginPercentage = totalDenominator > 0 ? (totalEffectiveMarginValue / totalDenominator) * 100 : 0;
        const empIdx = baseParams.findIndex(p => p.id === ExpressionSystem.formatParameterId(['effectiveMarginPercentage']));
        if (empIdx >= 0) {
            baseParams[empIdx].value = totalEffectiveMarginPercentage;
        }

        baseParams.push(...getGeneralityParameters(generalities, t));

        return baseParams;
    }, [totalsRow, rows, elementTypes, groupedParams, t, decennialInsuranceExpression, commissionExpression, defaultCommissionMarginPercentage, totalNetExcludingTax, linesTotalNetExcludingTax, totalExcludingTax, totalIncludingTax, budgetDiscount, defaultDiscountPercentage, defaultMarginPercentage, defaultDecennialInsurancePercentage, defaultTaxValue, generalities]);

    const evaluatedConfigs = useMemo(() => {
        const configMap = new Map<string, { expression: string; value: number; stringValue: string | null; plainValue?: number | null; plainTextValue?: string | null; valueType: BudgetConfigValueType }>();
        allConfigDefs.forEach(def => {
            const config = configs.find(c => c.key === def.key);
            const expression = config !== undefined ? config.expression : (def.defaultExpression ?? '');
            const plainValue = config?.value;
            const plainTextValue = config?.textValue;
            const valueType = config?.valueType ?? BudgetConfigValueType.NUMBER;
            configMap.set(def.key, { expression, value: 0, stringValue: null, plainValue, plainTextValue, valueType });
        });

        // Iterative evaluation: resolve cross-references
        const maxIterations = 10;
        for (let iter = 0; iter < maxIterations; iter++) {
            let changed = false;
            allConfigDefs.forEach(def => {
                const entry = configMap.get(def.key)!;
                const isText = entry.valueType === BudgetConfigValueType.TEXT;

                // If no expression, use plain value (numeric or text)
                if (!entry.expression) {
                    if (isText) {
                        const newString = entry.plainTextValue ?? null;
                        if (newString !== entry.stringValue) {
                            entry.stringValue = newString;
                            changed = true;
                        }
                    } else {
                        const newValue = entry.plainValue ?? 0;
                        if (newValue !== entry.value) {
                            entry.value = newValue;
                            changed = true;
                        }
                    }
                    return;
                }

                const configParams: ExpressionParameter[] = allConfigDefs
                    .filter(d => d.key !== def.key)
                    .map(d => {
                        const other = configMap.get(d.key)!;
                        return {
                            id: ExpressionSystem.formatParameterId([d.key]),
                            name: d.name,
                            value: other.valueType === BudgetConfigValueType.TEXT
                                ? (other.stringValue ?? '')
                                : other.value,
                        };
                    });

                const params = [...totalsParams, ...configParams];

                const result = ExpressionSystem.evaluate(entry.expression, params);
                if (isText) {
                    const newString = result.error ? null : (result.stringValue ?? ExpressionSystem.numberToStr(result.value));
                    if (newString !== entry.stringValue) {
                        entry.stringValue = newString;
                        changed = true;
                    }
                } else {
                    const newValue = result.error ? 0 : result.value;
                    if (newValue !== entry.value) {
                        entry.value = newValue;
                        changed = true;
                    }
                }
            });
            if (!changed) break;
        }

        return allConfigDefs.map(def => {
            const entry = configMap.get(def.key)!;
            return {
                key: def.key,
                expression: entry.expression,
                value: entry.value,
                stringValue: entry.stringValue,
                valueType: entry.valueType,
            };
        });
    }, [configs, allConfigDefs, totalsParams]);

    const getConfigParameters = useCallback((excludeKey: string): ExpressionParameter[] => {
        const configParams = allConfigDefs
            .filter(d => d.key !== excludeKey)
            .map(d => {
                const evaluated = evaluatedConfigs.find(c => c.key === d.key);
                const isText = evaluated?.valueType === BudgetConfigValueType.TEXT;
                return {
                    id: ExpressionSystem.formatParameterId([d.key]),
                    name: d.name,
                    value: isText ? (evaluated?.stringValue ?? '') : (evaluated?.value ?? 0),
                };
            });
        return [...totalsParams, ...configParams];
    }, [evaluatedConfigs, allConfigDefs, totalsParams]);

    const buildConfigsArray = useCallback((overrideKey: string, overrides: { expression?: string; value?: number | null; textValue?: string | null }): BudgetConfigDto[] => {
        return configs.map(c => {
            const normalized: BudgetConfigDto = { ...c, valueType: c.valueType ?? BudgetConfigValueType.NUMBER };
            if (c.key !== overrideKey) return normalized;
            return {
                ...normalized,
                expression: overrides.expression ?? normalized.expression,
                value: overrides.value !== undefined ? overrides.value : normalized.value,
                textValue: overrides.textValue !== undefined ? overrides.textValue : normalized.textValue,
            };
        });
    }, [configs]);

    const onConfigChange = useCallback((key: string, expression: string | null) => {
        dispatch(updateBudget({ configs: buildConfigsArray(key, { expression: expression ?? '' }) }));
    }, [buildConfigsArray, dispatch]);

    const onConfigPlainValueChange = useCallback((key: string, value: number | null | undefined) => {
        const existing = configs.find(c => c.key === key);
        const evaluated = evaluatedConfigs.find(c => c.key === key);

        // Se já existe uma fórmula e o valor recebido coincide com o resultado avaliado dessa
        // fórmula, este onChange é apenas o eco do próprio resultado (não uma escrita do utilizador).
        // Neste caso, não apagar a fórmula.
        if (existing?.expression && evaluated) {
            const newRounded = MathUtils.round(value ?? 0, decimalPlaces);
            const evaluatedRounded = MathUtils.round(evaluated.value ?? 0, decimalPlaces);
            if (newRounded === evaluatedRounded) {
                return;
            }
        }

        dispatch(updateBudget({ configs: buildConfigsArray(key, { expression: '', value: value ?? 0 }) }));
    }, [buildConfigsArray, configs, evaluatedConfigs, decimalPlaces, dispatch]);

    const onRestoreConfigDefault = useCallback((key: string) => {
        const def = allConfigDefs.find(d => d.key === key);
        if (!def?.defaultExpression) return;
        dispatch(updateBudget({
            configs: buildConfigsArray(key, { expression: def.defaultExpression, value: null }),
        }));
    }, [allConfigDefs, buildConfigsArray, dispatch]);

    const onAddCustomConfig = useCallback((valueType: BudgetConfigValueType) => {
        const newKey = `custom_${Date.now()}`;
        const allConfigs = buildConfigsArray('', {});
        allConfigs.push({ key: newKey, expression: '', label: '', valueType });
        dispatch(updateBudget({ configs: allConfigs }));
    }, [buildConfigsArray, dispatch]);

    const onRemoveConfig = useCallback((key: string) => {
        const allConfigs = buildConfigsArray('', {}).filter(c => c.key !== key);
        dispatch(updateBudget({ configs: allConfigs }));
    }, [buildConfigsArray, dispatch]);

    const onConfigLabelChange = useCallback((key: string, label: string) => {
        const allConfigs = buildConfigsArray('', {}).map(c => {
            if (c.key === key) {
                return { ...c, label };
            }
            return c;
        });
        dispatch(updateBudget({ configs: allConfigs }));
    }, [buildConfigsArray, dispatch]);

    const onCustomConfigTextChange = useCallback((key: string, text: string) => {
        const existing = configs.find(c => c.key === key);
        const evaluated = evaluatedConfigs.find(c => c.key === key);

        // Se já existe fórmula e o texto recebido coincide com o resultado avaliado, ignorar
        // (é apenas eco do resultado da fórmula, não escrita do utilizador).
        if (existing?.expression && evaluated && (evaluated.stringValue ?? '') === text) {
            return;
        }

        dispatch(updateBudget({ configs: buildConfigsArray(key, { expression: '', textValue: text }) }));
    }, [buildConfigsArray, configs, evaluatedConfigs, dispatch]);

    const validateCircularRef = useCallback((key: string, expression: string): string | null => {
        const currentMentionId = ExpressionSystem.formatParameterId([key]);
        const formulasByMentionId = new Map<string, string | null>();
        allConfigDefs.forEach(def => {
            const mentionId = ExpressionSystem.formatParameterId([def.key]);
            const config = configs.find(c => c.key === def.key);
            formulasByMentionId.set(mentionId, config?.expression ?? null);
        });
        const hasCircular = ExpressionSystem.hasCircularReference(currentMentionId, expression, formulasByMentionId);
        return hasCircular ? t('common.formula_circular_reference') : null;
    }, [configs, t]);

    const defaultCacheData: SummaryCacheData = {
        columnsWidths: [],
        order: { field: 'number' as keyof SummaryRow, isOrderAsc: true },
    };

    const [cacheData, setCacheData] = useState<SummaryCacheData>(() => {
        return Storage.getObject(CACHE_KEY) ?? defaultCacheData;
    });

    const saveCacheData = (data: SummaryCacheData) => {
        setCacheData(data);
        Storage.setObject(CACHE_KEY, data);
    };

    const evaluatedData = useMemo(() => {
        const evaluatedRows = rows.map(row => {
            const params = getFormulaParameters(row, elementTypes, groupedParams, t);
            const decennialInsuranceValue = decennialInsuranceExpression
                ? evaluateFormula(decennialInsuranceExpression, params)
                : row.decennialInsuranceValue;
            const commissionValue = commissionExpression
                ? evaluateFormula(commissionExpression, params)
                : row.commissionValue;
            const totalCosts = row.totalCostsWithoutInsurance * (1 + row.marginPercentage / 100) + decennialInsuranceValue;
            const effectiveMarginValue = row.totalWithMargin - row.totalWithoutMargin - decennialInsuranceValue - commissionValue;
            const denominator = row.totalWithoutMargin + decennialInsuranceValue + commissionValue;
            const effectiveMarginPercentage = denominator > 0 ? (effectiveMarginValue / denominator) * 100 : 0;
            return {
                ...row,
                decennialInsuranceValue,
                commissionValue,
                totalCosts,
                effectiveMarginValue,
                effectiveMarginPercentage,
            };
        });

        const totalDecennialInsurance = evaluatedRows.reduce((sum, r) => sum + r.decennialInsuranceValue, 0);
        const totalCommission = evaluatedRows.reduce((sum, r) => sum + r.commissionValue, 0);
        const totalTotalCosts = evaluatedRows.reduce((sum, r) => sum + r.totalCosts, 0);
        const commercialDiscount = evaluatedConfigs.find(c => c.key === 'commercial_discount')?.value ?? 0;
        const totalEffectiveMarginValue = totalsRow.totalWithMargin - totalsRow.totalWithoutMargin - totalDecennialInsurance - totalCommission - commercialDiscount;
        const totalDenominator = totalsRow.totalWithoutMargin + totalDecennialInsurance + totalCommission;
        const totalEffectiveMarginPercentage = totalDenominator > 0 ? (totalEffectiveMarginValue / totalDenominator) * 100 : 0;

        const evaluatedTotals: SummaryRow = {
            ...totalsRow,
            decennialInsuranceValue: totalDecennialInsurance,
            commissionValue: totalCommission,
            totalCosts: totalTotalCosts,
            effectiveMarginValue: totalEffectiveMarginValue,
            effectiveMarginPercentage: totalEffectiveMarginPercentage,
            _commercialDiscount: commercialDiscount,
        };

        return { rows: evaluatedRows, totalsRow: evaluatedTotals };
    }, [rows, totalsRow, elementTypes, groupedParams, decennialInsuranceExpression, commissionExpression, evaluatedConfigs, t]);

    const sortedRows = useMemo(() => {
        const sorted = [...evaluatedData.rows];
        const { field, isOrderAsc } = cacheData.order;

        sorted.sort((a, b) => {
            const aVal = (a as SummaryRow)[field];
            const bVal = (b as SummaryRow)[field];

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return isOrderAsc ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal ?? '');
            const bStr = String(bVal ?? '');
            return isOrderAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        });

        return [evaluatedData.totalsRow, ...sorted];
    }, [evaluatedData, cacheData.order]);

    const columns: ListingTableColumn<SummaryRow>[] = useMemo(() => {
        const cols: ListingTableColumn<SummaryRow>[] = [
            {
                field: 'number',
                name: t('budgets.budget.summary.point'),
                minWidth: 120,
                width: 120,
                renderCell: (row) => {
                    if (row.isTotalsRow) return '';
                    return (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {onNavigateToLine && (
                                <IoOpenOutline
                                    style={{ cursor: 'pointer', flexShrink: 0 }}
                                    title={t('budgets.budget.summary.go_to_line')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onNavigateToLine(row.id);
                                    }}
                                />
                            )}
                            {row.number}
                        </span>
                    );
                },
            },
            {
                field: 'name',
                name: t('budgets.budget.summary.name'),
                minWidth: 150,
                width: 200,
                renderCell: (row) => row.isTotalsRow ? '' : (row.name || ''),
            },
            {
                field: 'quantity',
                name: t('budgets.budget.summary.quantity'),
                cellAlignment: 'right',
                minWidth: 80,
                width: 80,
                renderCell: (row) => row.isTotalsRow || row.quantity == null ? '' : fmtNum(Number(row.quantity)),
            },
            {
                field: 'unit',
                name: t('budgets.budget.summary.unit'),
                minWidth: 80,
                width: 80,
                renderCell: (row) => row.isTotalsRow ? '' : (row.unit || ''),
            },
        ];

        elementTypes.forEach(et => {
            et.units.forEach(unit => {
                const qtyKey = `et_${et.id}_qty_${unit.toLowerCase()}`;
                cols.push({
                    field: qtyKey as keyof SummaryRow,
                    name: `${et.name} (${unit})`,
                    cellAlignment: 'right',
                    minWidth: 100,
                    width: 120,
                    renderCell: (row) => {
                        const val = row[qtyKey];
                        if (val == null || val === 0) return `${fmtNum(0)} ${unit}`;
                        return `${fmtNum(Number(val))} ${unit}`;
                    },
                });
            });
            cols.push({
                field: `et_${et.id}_total` as keyof SummaryRow,
                name: `${t('budgets.budget.summary.total_price')} ${et.name}`,
                cellAlignment: 'right',
                minWidth: 120,
                width: 140,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: `${t('budgets.budget.summary.total_price')} ${et.name}`,
                    message: t('budgets.budget.summary.et_total_info'),
                }),
                renderCell: (row) => {
                    const val = row[`et_${et.id}_total`];
                    return <MoneyFormat value={val ?? 0} suffix={' €'} decimalScale={decimalPlaces} />;
                },
            });
        });

        const materialWeightParam = groupedParams.find(gp => gp.parameterName.toLowerCase().trim() === MATERIAL_WEIGHT_PARAM_NAME);
        const otherGroupedParams = groupedParams.filter(gp => gp.parameterName.toLowerCase().trim() !== MATERIAL_WEIGHT_PARAM_NAME);

        const buildGroupedParamColumn = (gp: GroupedParamInfo): ListingTableColumn<SummaryRow> => {
            const suffix = gp.unitSymbol ? ` ${gp.unitSymbol}` : '';
            return {
                field: gp.fieldKey as keyof SummaryRow,
                name: gp.parameterName,
                cellAlignment: 'right',
                minWidth: 100,
                width: 120,
                renderCell: (row) => {
                    const val = row[gp.fieldKey];
                    if (val == null || val === 0) return `${fmtNum(0)}${suffix}`;
                    return `${fmtNum(Number(val))}${suffix}`;
                },
            };
        };

        otherGroupedParams.forEach(gp => {
            cols.push(buildGroupedParamColumn(gp));
        });

        cols.push({
            field: 'totalWeight',
            name: t('budgets.budget.summary.total_weight'),
            cellAlignment: 'right',
            minWidth: 120,
            width: 140,
            onInfoIconClick: () => setCurrentInfoTooltip({
                title: t('budgets.budget.summary.total_weight'),
                message: t('budgets.budget.summary.total_weight_info'),
            }),
            renderCell: (row) => {
                if (row.totalWeight == null || row.totalWeight === 0) return `${fmtNum(0)} Kg`;
                return `${fmtNum(Number(row.totalWeight))} Kg`;
            },
        });

        if (materialWeightParam) {
            cols.push(buildGroupedParamColumn(materialWeightParam));
        }

        cols.push(
            {
                field: 'costPerKg',
                name: t('budgets.budget.summary.cost_per_kg'),
                cellAlignment: 'right',
                minWidth: 120,
                width: 140,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.cost_per_kg'),
                    message: t('budgets.budget.summary.cost_per_kg_info'),
                }),
                renderCell: (row) => {
                    if (row.costPerKg == null || row.costPerKg === 0) return '';
                    const cell = <MoneyFormat value={row.costPerKg} suffix={' €/Kg'} decimalScale={decimalPlaces} />;
                    const tooltip = `${fmt(row.totalWithoutMargin)} € ÷ ${fmt(row._materialWeight ?? 0)} Kg = ${fmt(row.costPerKg)} €/Kg`;
                    return withCalcTooltip(cell, tooltip, `costPerKg-${row.id}`);
                },
            },
            {
                field: 'salePerKg',
                name: t('budgets.budget.summary.sale_per_kg'),
                cellAlignment: 'right',
                minWidth: 120,
                width: 140,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.sale_per_kg'),
                    message: t('budgets.budget.summary.sale_per_kg_info'),
                }),
                renderCell: (row) => {
                    if (row.salePerKg == null || row.salePerKg === 0) return '';
                    const cell = <MoneyFormat value={row.salePerKg} suffix={' €/Kg'} decimalScale={decimalPlaces} />;
                    const tooltip = `${fmt(row.totalWithMargin)} € ÷ ${fmt(row._materialWeight ?? 0)} Kg = ${fmt(row.salePerKg)} €/Kg`;
                    return withCalcTooltip(cell, tooltip, `salePerKg-${row.id}`);
                },
            },
            {
                field: 'totalWithoutMargin',
                name: t('budgets.budget.summary.total_without_margin'),
                tooltip: t('budgets.budget.summary.total_without_margin_tooltip'),
                cellAlignment: 'right',
                minWidth: 130,
                width: 150,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.total_without_margin'),
                    message: t('budgets.budget.summary.total_without_margin_info'),
                }),
                renderCell: (row) => {
                    const cell = <MoneyFormat value={row.totalWithoutMargin} suffix={' €'} decimalScale={decimalPlaces} />;
                    if (row.isTotalsRow || row._salePriceUnit == null) return cell;
                    const tooltip = `PVU: ${fmt(row._grossUnitPrice)} € − ${fmt(row._discountValue)} € + ${fmt(row._childrenUnitSum)} € = ${fmt(row._salePriceUnit)} €\n${fmt(row._salePriceUnit)} € × ${row.quantity} = ${fmt(row.totalWithoutMargin)} €`;
                    return withCalcTooltip(cell, tooltip, `totalWithoutMargin-${row.id}`);
                },
            },
            {
                field: 'marginPercentage',
                name: t('budgets.budget.summary.margin_percentage'),
                cellAlignment: 'right',
                minWidth: 100,
                width: 120,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.margin_percentage'),
                    message: t('budgets.budget.summary.margin_percentage_info'),
                }),
                renderCell: (row) => {
                    if (row.isTotalsRow && row.marginPercentage === 0) return '';
                    const text = `${fmtNum(Number(row.marginPercentage))}%`;
                    if (row.isTotalsRow) {
                        const tooltip = `((${fmt(row.totalWithMargin)} ÷ ${fmt(row.totalWithoutMargin)}) − 1) × 100 = ${fmtNum(Number(row.marginPercentage))}%`;
                        return withCalcTooltip(<span>{text}</span>, tooltip, `marginPercentage-${row.id}`);
                    }
                    return text;
                },
            },
            {
                field: 'totalWithMargin',
                name: t('budgets.budget.summary.total_with_margin'),
                tooltip: t('budgets.budget.summary.total_with_margin_tooltip'),
                cellAlignment: 'right',
                minWidth: 130,
                width: 150,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.total_with_margin'),
                    message: t('budgets.budget.summary.total_with_margin_info'),
                }),
                renderCell: (row) => {
                    return <MoneyFormat value={row.totalWithMargin} suffix={' €'} decimalScale={decimalPlaces} />;
                },
            },
            {
                field: 'decennialInsuranceValue',
                name: t('budgets.budget.summary.decennial_insurance'),
                cellAlignment: 'right',
                minWidth: 150,
                width: 170,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.decennial_insurance'),
                    message: t('budgets.budget.summary.decennial_insurance_info'),
                }),
                renderCell: (row) => {
                    const cell = <MoneyFormat value={row.decennialInsuranceValue} suffix={' €'} decimalScale={decimalPlaces} />;
                    if (row.isTotalsRow || row._priceWithMargin == null) return cell;
                    const tooltip = `${fmt(row._priceWithMargin)} € × ${fmt(row._decennialInsurancePercentage)}% × ${row.quantity} = ${fmt(row.decennialInsuranceValue)} €`;
                    return withCalcTooltip(cell, tooltip, `decennialInsurance-${row.id}`);
                },
            },
            {
                field: 'commissionValue',
                name: t('budgets.budget.summary.commission'),
                cellAlignment: 'right',
                minWidth: 150,
                width: 170,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.commission'),
                    message: t('budgets.budget.summary.commission_info'),
                }),
                renderCell: (row) => {
                    const cell = <MoneyFormat value={row.commissionValue} suffix={' €'} decimalScale={decimalPlaces} />;
                    const tooltip = `${fmt(row.totalWithoutMargin)} € × ${fmt(defaultCommissionMarginPercentage)}% = ${fmt(row.commissionValue)} €`;
                    return withCalcTooltip(cell, tooltip, `commission-${row.id}`);
                },
            },
            {
                field: 'effectiveMarginValue',
                name: t('budgets.budget.summary.effective_margin_value'),
                tooltip: t('budgets.budget.summary.effective_margin_value_tooltip'),
                cellAlignment: 'right',
                minWidth: 130,
                width: 150,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.effective_margin_value'),
                    message: t('budgets.budget.summary.effective_margin_value_info'),
                }),
                renderCell: (row) => {
                    const cell = <MoneyFormat value={row.effectiveMarginValue} suffix={' €'} decimalScale={decimalPlaces} />;
                    const commercialDiscount = row._commercialDiscount ?? 0;
                    const tooltip = row.isTotalsRow
                        ? `${fmt(row.totalWithMargin)} € − ${fmt(row.totalWithoutMargin)} € − ${fmt(row.decennialInsuranceValue)} € − ${fmt(row.commissionValue)} € − ${fmt(commercialDiscount)} € = ${fmt(row.effectiveMarginValue)} €`
                        : `${fmt(row.totalWithMargin)} € − ${fmt(row.totalWithoutMargin)} € − ${fmt(row.decennialInsuranceValue)} € − ${fmt(row.commissionValue)} € = ${fmt(row.effectiveMarginValue)} €`;
                    return withCalcTooltip(cell, tooltip, `effectiveMarginValue-${row.id}`);
                },
            },
            {
                field: 'effectiveMarginPercentage',
                name: t('budgets.budget.summary.effective_margin_percentage'),
                tooltip: t('budgets.budget.summary.effective_margin_percentage_tooltip'),
                cellAlignment: 'right',
                minWidth: 100,
                width: 120,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.effective_margin_percentage'),
                    message: t('budgets.budget.summary.effective_margin_percentage_info'),
                }),
                renderCell: (row) => {
                    if (row.effectiveMarginPercentage === 0) return '';
                    const denominator = row.totalWithoutMargin + row.decennialInsuranceValue + row.commissionValue;
                    const text = `${fmtNum(Number(row.effectiveMarginPercentage))}%`;
                    const commercialDiscount = row._commercialDiscount ?? 0;
                    const tooltip = row.isTotalsRow && commercialDiscount !== 0
                        ? `(${fmt(row.totalWithMargin)} € − ${fmt(row.totalWithoutMargin)} € − ${fmt(row.decennialInsuranceValue)} € − ${fmt(row.commissionValue)} € − ${fmt(commercialDiscount)} €) ÷ ${fmt(denominator)} € × 100 = ${fmtNum(Number(row.effectiveMarginPercentage))}%`
                        : `${fmt(row.effectiveMarginValue)} € ÷ ${fmt(denominator)} € × 100 = ${fmtNum(Number(row.effectiveMarginPercentage))}%`;
                    return withCalcTooltip(<span>{text}</span>, tooltip, `effectiveMarginPercentage-${row.id}`);
                },
            },
            {
                field: 'totalCostsWithoutInsurance',
                name: t('budgets.budget.summary.total_costs_without_insurance'),
                tooltip: t('budgets.budget.summary.total_costs_without_insurance_tooltip'),
                cellAlignment: 'right',
                minWidth: 130,
                width: 150,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.total_costs_without_insurance'),
                    message: t('budgets.budget.summary.total_costs_without_insurance_info'),
                }),
                renderCell: (row) => {
                    const cell = <MoneyFormat value={row.totalCostsWithoutInsurance} suffix={' €'} decimalScale={decimalPlaces} />;
                    const parts = elementTypes.map(et => `${et.name}: ${fmt(row[`et_${et.id}_total`] ?? 0)} €`).join(' + ');
                    const tooltip = parts || `${fmt(row.totalCostsWithoutInsurance)} €`;
                    return withCalcTooltip(cell, tooltip, `totalCostsWithoutInsurance-${row.id}`);
                },
            },
            {
                field: 'totalCosts',
                name: t('budgets.budget.summary.total_costs'),
                tooltip: t('budgets.budget.summary.total_costs_tooltip'),
                cellAlignment: 'right',
                minWidth: 130,
                width: 150,
                onInfoIconClick: () => setCurrentInfoTooltip({
                    title: t('budgets.budget.summary.total_costs'),
                    message: t('budgets.budget.summary.total_costs_info'),
                }),
                renderCell: (row) => {
                    const cell = <MoneyFormat value={row.totalCosts} suffix={' €'} decimalScale={decimalPlaces} />;
                    const tooltip = `${fmt(row.totalCostsWithoutInsurance)} € × (1 + ${fmtNum(Number(row.marginPercentage))}%) + ${fmt(row.decennialInsuranceValue)} € = ${fmt(row.totalCosts)} €`;
                    return withCalcTooltip(cell, tooltip, `totalCosts-${row.id}`);
                },
            },
        );

        return orderColumns(cols, columnsOrder);
    }, [elementTypes, groupedParams, t, decimalPlaces, columnsOrder, decennialInsuranceExpression, commissionExpression, evaluatedData, rows, totalsRow, defaultCommissionMarginPercentage]);

    const onDecennialInsuranceChange = (expression: string | null) => {
        const update: Partial<BudgetData> = { decennialInsuranceExpression: expression ?? undefined };
        dispatch(updateBudget(update));
    };

    const onCommissionChange = (expression: string | null) => {
        const update: Partial<BudgetData> = { commissionExpression: expression ?? undefined };
        dispatch(updateBudget(update));
    };

    const onOrder = (orderData: OrderData<SummaryRow>) => {
        saveCacheData({ ...cacheData, order: orderData });
    };

    const onColResize = (columnsWidths: ColumnWidth<SummaryRow>[]) => {
        saveCacheData({ ...cacheData, columnsWidths });
    };

    const rowStyle = (_row: SummaryRow, _col: ListingTableColumn<SummaryRow>, rowIndex: number, _colIndex: number): CSSProperties => {
        if (rowIndex === 0) {
            return {
                fontWeight: 'bold',
            };
        }
        return {};
    };

    return (
        <div className={styles.container}>
            <Row className="mb-4 align-items-end">
                {orderedConfigItems.map(item => {
                    const key = item.key;
                    const storedConfig = configs.find(c => c.key === key);
                    const evaluated = evaluatedConfigs.find(c => c.key === key);

                    const defaultName = item.kind === 'predefined' ? t(item.def.translationKey) : '';
                    const labelValue = storedConfig?.label ?? '';
                    const displayName = (labelValue && labelValue.trim().length > 0) ? labelValue : (defaultName || t('budgets.budget.summary.custom_config'));

                    const fullDef = item.kind === 'predefined' ? allConfigDefs.find(d => d.key === key) : undefined;
                    const hasDefault = Boolean(fullDef?.defaultExpression);

                    const isText = item.kind === 'custom' && (item.config.valueType ?? BudgetConfigValueType.NUMBER) === BudgetConfigValueType.TEXT;
                    const hasExpression = item.kind === 'custom' && Boolean(item.config.expression && item.config.expression.trim().length > 0);
                    const unitPrefix = item.kind === 'predefined' ? item.def.unitPrefix : '€';

                    return (
                        <Col sm={12} md={12} lg={6} xl={4} xxl={3} key={key} className="mb-4">
                            <div className={styles.configLabel}>
                                <input
                                    type="text"
                                    className={styles.configLabelInput}
                                    value={labelValue || defaultName}
                                    placeholder={t('budgets.budget.summary.custom_config_placeholder')}
                                    disabled={isDetails}
                                    onChange={(e) => onConfigLabelChange(key, e.target.value)}
                                />
                                {item.kind === 'predefined' && hasDefault && !isDetails && (
                                    <button
                                        type="button"
                                        className={styles.restoreButton}
                                        onClick={() => setRestoreConfirmKey(key)}
                                        title={t('budgets.budget.summary.restore_default_tooltip')}
                                    >
                                        <IoRefresh />
                                    </button>
                                )}
                                {!isDetails && (
                                    <button
                                        type="button"
                                        className={styles.customConfigRemove}
                                        onClick={() => onRemoveConfig(key)}
                                        title={t('common.remove')}
                                    >
                                        <IoTrash />
                                    </button>
                                )}
                            </div>
                            {isText
                                ? (
                                    <TextInput
                                        value={evaluated?.stringValue ?? (storedConfig?.textValue ?? '')}
                                        disabled={isDetails || hasExpression}
                                        onChange={(e) => {
                                            if (isDetails) return;
                                            onCustomConfigTextChange(key, e.target.value);
                                        }}
                                        expressionButton={{
                                            title: displayName,
                                            value: evaluated?.expression ?? '',
                                            loadParameters: () => getConfigParameters(key),
                                            onChange: (expr) => onConfigChange(key, expr),
                                            onValidate: (expr) => validateCircularRef(key, expr),
                                            disabled: isDetails,
                                            hideResult: true,
                                        }}
                                    />
                                )
                                : (
                                    <MoneyInput
                                        value={evaluated?.value ?? 0}
                                        decimalScale={decimalPlaces}
                                        hideUnitsDrop
                                        disabled={isDetails}
                                        unitPrefix={unitPrefix}
                                        expressionButton={{
                                            title: displayName,
                                            value: evaluated?.expression ?? '',
                                            loadParameters: () => getConfigParameters(key),
                                            onChange: (expr) => onConfigChange(key, expr),
                                            onValidate: (expr) => validateCircularRef(key, expr),
                                            disabled: isDetails,
                                        }}
                                        onChange={(val) => {
                                            if (isDetails) return;
                                            onConfigPlainValueChange(key, val);
                                        }}
                                    />
                                )}
                        </Col>
                    );
                })}
                {!isDetails && <Col sm={12} md={12} lg={6} xl={4} xxl={3} className="mb-4 d-flex align-items-end">
                    <Dropdown>
                        <Dropdown.Toggle as={Button} variant="third">
                            <IoAddSharp fontSize={18} /> {t('budgets.budget.summary.add_config')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => onAddCustomConfig(BudgetConfigValueType.NUMBER)}>
                                {t('budgets.budget.summary.add_config_numeric')}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => onAddCustomConfig(BudgetConfigValueType.TEXT)}>
                                {t('budgets.budget.summary.add_config_text')}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>}
            </Row>
            <ListingTable
                columns={columns}
                columnsWidths={cacheData.columnsWidths}
                rows={sortedRows}
                hiddenColumns={hiddenColumns as any}
                order={cacheData.order}
                onOrder={onOrder}
                onColResize={onColResize}
                rowStyle={rowStyle}
                noItemsText={t('messages.no_info_to_show')}
                colorRow=''
            />
            <InformationModal
                title={currentInfoTooltip?.title}
                message={currentInfoTooltip?.message || ''}
                onClose={() => setCurrentInfoTooltip(null)}
                isOpen={Boolean(currentInfoTooltip)}
            />
            {restoreConfirmKey && (
                <YesNoModal
                    isOpen={true}
                    title={t('budgets.budget.summary.restore_default_title')}
                    message={t('budgets.budget.summary.restore_default_message')}
                    onClose={(yes) => {
                        if (yes && restoreConfirmKey) onRestoreConfigDefault(restoreConfirmKey);
                        setRestoreConfirmKey(null);
                    }}
                />
            )}
        </div>
    );
}

export default BudgetSummary;
